/**
 * Returns a human-readable "N days ago" message and a Tailwind color class
 * based on how many days have passed since `dateStr`.
 *
 * Color thresholds:
 *  0-2 days  → text-muted-foreground (neutral)
 *  3-4 days  → text-slate-700        (mild warning)
 *  5-6 days  → text-orange-600       (moderate warning)
 *  7+ days   → text-red-600 font-medium (urgent)
 */
export const getDaysAgoAndColor = (
  dateStr: string | undefined,
  t: (key: string) => string,
): { message: string; colorClass: string } => {
  if (!dateStr) return { message: 'N/A', colorClass: 'text-muted-foreground' };

  const createdDate = new Date(dateStr);
  const currentDate = new Date();

  // Use UTC dates for accurate comparison (matches backend logic)
  const createdUTC = Date.UTC(createdDate.getUTCFullYear(), createdDate.getUTCMonth(), createdDate.getUTCDate());
  const currentUTC = Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.getUTCDate());

  const diffTime = currentUTC - createdUTC;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  let colorClass = 'text-muted-foreground';
  let message = '';

  if (diffDays === 0) {
    colorClass = 'text-muted-foreground';
    message = '';
  } else if (diffDays === 1) {
    colorClass = 'text-muted-foreground';
    message = `1 ${t('day_ago') || 'day ago'}`;
  } else if (diffDays === 2) {
    colorClass = 'text-muted-foreground';
    message = `2 ${t('days_ago') || 'days ago'}`;
  } else if (diffDays >= 3 && diffDays <= 4) {
    colorClass = 'text-slate-700';
    message = `${diffDays} ${t('days_ago') || 'days ago'}`;
  } else if (diffDays >= 5 && diffDays <= 7) {
    colorClass = 'text-orange-600';
    message = `${diffDays} ${t('days_ago') || 'days ago'}`;
  } else if (diffDays > 7) {
    colorClass = 'text-red-600 font-medium';
    message = `${diffDays} ${t('days_ago') || 'days ago'}`;
  } else {
    colorClass = 'text-muted-foreground';
    message = '';
  }

  return { message, colorClass };
};
